import React, { useEffect } from 'react';
import { useLanguage } from '../../src/functions/useLanguage';
import { wt } from '../i18n';
import { websitePath } from '../lib/routes';

const META = {
  title: 'Terms of Service | KodaCare',
  description: 'KodaCare Terms of Service — your rights and obligations when using the KodaCare platform.',
  lastUpdated: '12 June 2026',
};

export function TermsOfService() {
  const { language } = useLanguage();
  const t = (key: Parameters<typeof wt>[1]) => wt(language, key);

  useEffect(() => {
    const prev = document.title;
    document.title = META.title;

    const setMeta = (selector: string, attr: string, content: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, selector.match(/"([^"]+)"/)?.[1] ?? ''); document.head.appendChild(el); }
      el.content = content;
    };

    setMeta('meta[name="description"]', 'name', META.description);
    setMeta('meta[property="og:title"]', 'property', META.title);
    setMeta('meta[property="og:description"]', 'property', META.description);
    setMeta('meta[property="og:type"]', 'property', 'website');
    setMeta('meta[property="og:url"]', 'property', 'https://kodacare.pet/terms');
    setMeta('meta[property="og:image"]', 'property', 'https://kodacare.pet/icons/og-image.png');
    setMeta('meta[name="twitter:card"]', 'name', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'name', META.title);
    setMeta('meta[name="twitter:description"]', 'name', META.description);
    setMeta('meta[name="twitter:image"]', 'name', 'https://kodacare.pet/icons/og-image.png');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    const prevCanonical = canonical.href;
    canonical.href = 'https://kodacare.pet/terms';

    return () => {
      document.title = prev;
      if (canonical) canonical.href = prevCanonical;
    };
  }, []);

  return (
    <main className="max-w-3xl mx-auto px-6 py-16 text-gray-800">
      <h1 className="text-3xl font-black text-gray-900 mb-2">{t('legal.termsTitle')}</h1>
      <p className="text-sm text-gray-400 mb-10">{t('legal.lastUpdated')}: {META.lastUpdated}</p>

      <div className="space-y-8 text-sm leading-relaxed">

        <section>
          <h2 className="text-lg font-bold mb-2">1. Acceptance of Terms</h2>
          <p>By creating an account or using KodaCare ("the Service"), you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree, please do not use the Service.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">2. Description of Service</h2>
          <p>KodaCare is a pet health companion application designed to help pet parents track daily health metrics, manage medications and care routines, organise vet records, and access practical guidance. The Service is not a substitute for professional veterinary advice, diagnosis, or treatment.</p>
        </section>

        <section id="medical-disclaimer">
          <h2 className="text-lg font-bold mb-2">3. Medical Disclaimer</h2>
          <p>KodaCare is a pet health <strong>tracking and management tool only</strong>. It does not constitute veterinary advice, diagnosis, or treatment. The information and data within KodaCare are intended to support — not replace — the guidance of your veterinarian or qualified pet healthcare provider.</p>
          <p className="mt-2">You must not make clinical decisions about your pet solely based on data or content within this app. Always consult your vet regarding changes to your pet's medication, diet, or care plan. In an emergency, contact your local emergency vet immediately.</p>
          <p className="mt-2">The Wellhouse Studio Ltd accepts no liability for any harm arising from reliance on KodaCare as a substitute for professional veterinary care.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">4. Eligibility</h2>
          <p>You must be at least 16 years of age to use the Service. By using KodaCare, you represent that you meet this requirement.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">5. Account Responsibilities</h2>
          <p>You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. If you suspect unauthorised access, please report it immediately using the Help section in the app.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">6. Your Data</h2>
          <p>You retain ownership of all health and pet-care data you enter into KodaCare. We process this data solely to provide the Service. For full details on how we collect, store, and protect your data, please read our <a href={websitePath('/privacy')} className="text-primary-brown hover:underline">Privacy Policy</a>.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">7. Acceptable Use</h2>
          <p>You agree not to misuse the Service. Prohibited activities include, but are not limited to: attempting to gain unauthorised access to other users' data, uploading malicious content, using the Service for any unlawful purpose, or impersonating another person or healthcare professional.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">8. Subscriptions & Payments</h2>
          <p>Certain features of KodaCare may require a paid subscription. Subscription fees are charged in advance on a recurring basis. You may cancel at any time; cancellations take effect at the end of the current billing period. Refunds are handled in accordance with applicable consumer law.</p>
          <p className="mt-2">Companion plans are also subject to fair use controls to protect service quality and platform stability. For Expert Companion, pet profiles are unlimited, and co-carer access is available up to 10 total co-carer accounts per owner account. We may apply temporary rate limits or require a usage review for activity that is automated, abusive, unlawful, or materially degrades service for other users.</p>
          <p className="mt-2">Where possible, we will provide notice before enforcement action. We may take immediate action without notice where needed for security, fraud prevention, abuse mitigation, or service protection.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">9. Intellectual Property</h2>
          <p>All content, trademarks, software, and design elements within KodaCare are the property of The Wellhouse Studio Ltd or its licensors. You may not copy, reproduce, or distribute any part of the Service without our express written permission.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">10. Limitation of Liability</h2>
          <p>To the fullest extent permitted by law, KodaCare and The Wellhouse Studio Ltd shall not be liable for any indirect, incidental, or consequential damages arising from your use of the Service. Our total liability shall not exceed the amount you paid to us in the 12 months preceding the claim.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">11. Termination</h2>
          <p>We reserve the right to suspend or terminate your account if you violate these Terms. You may delete your account at any time from the Settings page within the app.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">12. Changes to Terms</h2>
          <p>We may update these Terms from time to time. We will notify you of material changes via email or an in-app notice. Continued use of the Service after changes take effect constitutes acceptance of the revised Terms.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">13. Governing Law</h2>
          <p>These Terms are governed by the laws of Portugal. Any disputes shall be subject to the exclusive jurisdiction of the courts of Portugal.</p>
        </section>

      </div>
    </main>
  );
}
